import { inject, Injector } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { get } from 'lodash';
import { from, map, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { ChatDataService } from 'src/shared/services/chat.data.service/chat.data.service';
import { environment } from 'src/environments/environment';
import { accountexternal } from 'src/shared/services/client/client';
import { Channel, ChannelFilters, ConnectionOpen } from 'stream-chat';
import { ChannelService, ChatClientService, ClientEvent, StreamMessage } from 'stream-chat-angular';
import { withAiChatStatus } from '../features/ai-chat-status/ai-chat-status.feature';
import { withLatestMessageTime } from '../features/chat-latest-message-time/chat-latest-message-time.feature';
import { withRecommendedProductsOnFrozen } from '../features/chat-recommended-products/chat-recommended-products.feature';
import { withActiveChannelStaffMember } from '../features/chat-staff-member/chat-staff-member.feature';
import { withUnreadCount } from '../features/chat-unread-count/chat-unread-count.feature';
import { withTenant } from '../features/tenant/tenant.feature';
import { withUser } from '../features/user/user.feature';

export const ChatConnectionStore = signalStore(
  withState({
    connectionStatus: 'idle' as 'idle' | 'connecting' | 'connected' | 'error',
    connectedUser: null as { doc_id: string; name: string } | null,
    channels: [] as Channel[],
    activeChannel: null as Channel | null,
    error: null as string | null | undefined | unknown,
  }),
  withUser(),
  withTenant(),
  withActiveChannelStaffMember(),
  withLatestMessageTime(),
  withUnreadCount(),
  withAiChatStatus(),
  withRecommendedProductsOnFrozen(),
  withMethods((store) => {
    const client = inject(ChatClientService);
    const channelService = inject(ChannelService);
    const chatDataService = inject(ChatDataService);

    /**
     * Event handler for when a user is added to a channel.
     * This will add the channel to the stream channel list and watch the channel.
     * It will ignore new triage channels and only add messaging channels.
     * @param clientEvent
     * @param channelListSetter
     * @returns void
     */
    const customAddedToChannelNotificationHandler = async (
      clientEvent: ClientEvent,
      channelListSetter: (channels: Channel[]) => void
    ) => {
      const channelResponse = clientEvent!.event!.channel!;
      const isCallTriage = get(channelResponse, 'call_triage', false);

      // Only add messaging channels and ignore call triage channels.
      if (channelResponse.type !== 'messaging' || isCallTriage) {
        return Promise.resolve();
      }
      const newChannel = client.chatClient.channel(channelResponse.type, channelResponse.id);
      try {
        await newChannel.watch();
        const existingChannels = channelService.channels;
        channelListSetter([newChannel, ...existingChannels]);
      } catch (error) {
        console.error('Failed to watch channel', error);
      }
    };

    const setActiveChannel$ = rxMethod<Channel | undefined>(
      pipe(
        tapResponse({
          next: (next) => {
            if (next) {
              channelService.setAsActiveChannel(next);
              next.watch();
              store.updateUnreadCount();
              store.updateLatestMessage();
            }
            patchState(store, { activeChannel: next });
          },
          error: (error) => {
            patchState(store, { error });
          },
        })
      )
    );

    return {
      connectUser(user: { doc_id: string; name: string }): Observable<void | ConnectionOpen> {
        const userPayload = {
          id: user?.doc_id || '',
          name: user.name,
          image: `https://getstream.io/random_png/?name=${user.name}`,
        };

        return of(client?.chatClient?.user).pipe(
          switchMap((currentUser) => {
            if (!currentUser || (currentUser && currentUser?.['id'] !== userPayload.id)) {
              // Requires connection to stream chat service
              return chatDataService.GetToken().pipe(
                switchMap((result) => {
                  if (!result?.token) {
                    throw new Error('No token received from GetToken');
                  }
                  if (currentUser && currentUser?.['id'] !== userPayload.id) {
                    // If connected to a different user, we need to disconnect them first.
                    return from(client.disconnectUser()).pipe(
                      switchMap(() => {
                        channelService.reset();
                        return from(client.init(environment.getstream?.apiKey || '', userPayload, result.token));
                      })
                    );
                  }
                  return from(client.init(environment.getstream?.apiKey || '', userPayload, result.token));
                })
              );
            }
            // if the user is already connected, we can do nothing.
            return of(undefined);
          }),
          tap(() => {
            channelService.customAddedToChannelNotificationHandler = customAddedToChannelNotificationHandler.bind(this);
          }),
          tapResponse({
            next: () => {
              patchState(store, {
                connectionStatus: 'connected',
                connectedUser: user,
                error: null,
              });
            },
            error: (error) => {
              patchState(store, { connectionStatus: 'error', error });
            },
          })
        );
      },

      async disconnect() {
        if (client && client.hasOwnProperty('disconnectUser') && typeof client.disconnectUser === 'function') {
          await client.disconnectUser();
        }
        channelService.reset();
        patchState(store, { connectionStatus: 'idle' });
      },

      setChannels(user: accountexternal.GetUserResponse) {
        const filters = {
          type: 'messaging',
          members: { $in: [user.doc_id] },
          hidden: false,
          call_triage: false,
        } as ChannelFilters;
        return from(channelService.init(filters)).pipe(
          tapResponse(
            (next) => {
              patchState(store, { channels: next });
            },
            (error) => {
              patchState(store, { error });
            }
          )
        );
      },

      setActiveChannel(channel: Channel | undefined): void {
        if (!channel) return;
        channelService.setAsActiveChannel(channel);
        patchState(store, { activeChannel: channel });
        channel.watch({ presence: true });
        store.updateUnreadCount();
        store.updateLatestMessage();
      },

      setActiveChannelListener(injector?: Injector) {
        setActiveChannel$(channelService.activeChannel$, { injector: injector });
      },

      getChannel(channelId: string): Channel | undefined {
        return channelService.channels.find((c) => c.id === channelId);
      },

      /**
       * Creates, watches, and sets a channel as active by ID.
       * Use when the channel may not be in the list yet (e.g. newly created).
       */
      watchAndSetActiveChannel(channelId: string): Observable<Channel> {
        const channel = client.chatClient.channel('messaging', channelId);
        return from(channel.watch()).pipe(
          map(() => {
            if (!channelService.channels.some((c) => c.id === channelId)) {
              channelService.addChannel(channel);
            }
            patchState(store, { channels: channelService.channels, activeChannel: channel });
            channelService.setAsActiveChannel(channel);
            channel.watch({ presence: true });
            store.updateUnreadCount();
            store.updateLatestMessage();
            return channel;
          })
        );
      },

      sendMessageToActiveChannel(text: string): Observable<StreamMessage> {
        return from(channelService.sendMessage(text));
      },

      sendMessageToChannel(text: string, channelId: string) {
        const channel = channelService.channels.find((c) => c.id === channelId);
        if (!channel) {
          throw new Error(`Channel with id ${channelId} not found`);
        }
        return from(channel.sendMessage({ text }));
      },
    };
  }),
  withHooks((store) => ({
    onDestroy: async () => {
      await store.disconnect();
    },
  }))
);
